import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonUtilityService } from '@app/services/common-utility.service';
import { HotelReservationCheckInService } from '@app/services/hotel-reservation-check-in.service';
import { MsGuestsService } from '@upr-web-primer/ms-guests';
import { NotificationService } from '@upr-web-primer/notification';
import { WINDOW } from '@upr-web-primer/window';
import { DateTime } from 'luxon';
import { Subject, takeUntil } from 'rxjs';
import { environment } from '../../../environments/environment';
@Component({
  selector: 'app-hotel-reservation',
  templateUrl: './hotel-reservation.component.html',
  styleUrl: './hotel-reservation.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HotelReservationComponent implements OnInit, OnDestroy {
  pageLabels: any;
  targetUrl: any;
  showVacationPackages: any;
  showUpcomingHotelReservations = false;
  constructor(
    @Inject('GlobalDefaults') public globalDefault: any,
    @Inject('AppRoutes') public appRoutes: any,
    private readonly guestsService: MsGuestsService,
    private readonly common: CommonUtilityService,
    private readonly hotelReservationCheckInService: HotelReservationCheckInService,
    private readonly notification: NotificationService,
    @Inject('TriggerDDLMappings') public triggerDDLMappings: any,
    @Inject(WINDOW) private readonly window: any,
    private readonly cdRef: ChangeDetectorRef,
  ) {}
  responseData: any;
  numberOfNights: string = '';
  errorData: any;
  private upcomingReservation$ = new Subject<void>();

  /**
   * ngOnInit - initial methods are called
   */
  ngOnInit(): void {
    this.showUpcomingHotelReservations =
      environment.config?.dashboard?.showUpcomingHotelReservations;
    this.pageLabels = this.common?.getResolvedPageData();
    this.showVacationPackages =
      environment.config?.dashboard?.showVacationPackages;
    this.getHotelReservationData();
  }
  /**
   * getReservationDetails - Navigates to the hotel reservation details page.
   */
  getReservationDetails(reservationType: any): any {
    switch (reservationType) {
      case this.globalDefault.HOTEL:
        this.common.changeRoute(this.appRoutes.RESERVATION);
        this.targetUrl = this.common.getTargetUrl(this.appRoutes.RESERVATION);
        this.ddlEvents(
          this.pageLabels?.['reservation.hotelreservationheading'],
          this.globalDefault.HOTEL,
          this.targetUrl,
        );
        break;
      case this.globalDefault.VACATION: {
        this.common.changeRoute(this.appRoutes.VIEW_VACATION_PURCHASES);
        const targetUrl = this.common.getTargetUrl(
          this.appRoutes.VIEW_VACATION_PURCHASES,
        );
        this.ddlEvents(
          this.pageLabels?.['dashboard.vacation_packages_label'],
          this.globalDefault.VACATION,
          targetUrl,
        );
        break;
      }
    }
  }

  /**
   * getHotelReservationData - Fetches hotel reservation data for the guest
   * the nearest upcoming reservation based on check-in date
   */
  getHotelReservationData(): void {
    const guestId = this.guestsService.helper.getGuestId();
    this.hotelReservationCheckInService
      .getHotelReservation(guestId)
      ?.pipe(takeUntil(this.upcomingReservation$))
      .subscribe(
        (res: any) => {
          const responseData = res;
          const today = DateTime.now().startOf('day');
          const upcomingReservations = responseData
            .filter(
              (res: any) => DateTime.fromISO(res?.check_out_date) >= today,
            )
            .sort(
              (a: any, b: any) =>
                DateTime.fromISO(a?.check_out_date).toJSDate().getTime() -
                DateTime.fromISO(b?.check_out_date).toJSDate().getTime(),
            );

          this.responseData =
            upcomingReservations.length > 0 ? upcomingReservations[0] : null;
          this.cdRef.markForCheck();
        },
        (err: any) => {
          this.errorData = err;
          const commerceGenericErrorMessage =
            this.common.removeHyphensFromCommerceAPI(
              this.errorData?.error?.errors?.[0]?.errorCode,
              this.pageLabels,
              this.globalDefault,
            );
          this.notification?.show(commerceGenericErrorMessage);
          this.cdRef.markForCheck();
        },
      );
  }
  /**
   * getDateLabel - Converts a date string into a label
   */
  getDateLabel(dateString: string): string {
    return this.hotelReservationCheckInService.convertDateLabel(dateString);
  }
  /**
   * getNightsBetweenDates - Calculates the number of nights between two dates.
   */
  getNightsBetweenDates(startStr: string, endStr: string): string {
    return this.hotelReservationCheckInService.getTotalNights(startStr, endStr);
  }
  /**
   * getFirstRoomName - Extracts and returns the room name from the first room entry.
   */
  getFirstRoomName(): string {
    if (
      this.responseData?.booking_rooms?.length > 0 &&
      this.responseData.booking_rooms?.[0]?.room_type_name
    ) {
      return this.responseData.booking_rooms?.[0]?.room_type_name;
    }
    return '';
  }

  /**
   * getRoomTypeCode - Extracts and returns the room name from the first room code entry.
   */
  getRoomTypeCode(): string {
    if (this.responseData?.booking_rooms?.length > 0) {
      return String(
        this.responseData.booking_rooms.indexOf(
          this.responseData.booking_rooms[0],
        ) + 1,
      );
    }
    return '';
  }
  /**
   * ngOnDestroy - Angular lifecycle hook to clean up observables and prevent memory leaks.
   */
  ngOnDestroy(): void {
    this.upcomingReservation$.next();
    this.upcomingReservation$.complete();
  }

  /**
   * DDL Events
   */
  ddlEvents(label: string, type: any, url: any): void {
    this.common.invokeFireEvent(
      label,
      'Button',
      'Click',
      null,
      new Date().toISOString(),
      type,
      url,
      this.window?.digitalData?.page?.category?.primaryCategory ?? '',
      this.window?.digitalData?.page?.category?.subCategory1 ?? '',
    );
  }
}
